import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Phone, Menu, X, ArrowRight } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/data/site";

const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} className="flex items-center shrink-0" data-testid="nav-logo" aria-label="LAD CO Electrical home">
    <img src="/logo.png" alt="LAD CO Electrical — Level 2 Accredited Service Provider, Northern Rivers NSW" className="h-16 lg:h-20 w-auto" />
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
    <header className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-[0_1px_12px_rgba(11,15,14,0.06)]" data-testid="main-nav">
      <div className="container-site">
        <div className="flex items-center justify-between h-24">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `font-heading text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 pb-1 border-b-2 ${
                    isActive ? "text-volt border-volt" : "text-ink/70 border-transparent hover:text-ink"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/services#level2"
              className="hidden xl:inline-flex items-center gap-1.5 bg-volt text-white font-heading text-xs font-bold uppercase tracking-wider px-4 py-3 rounded-md shadow-[0_6px_20px_rgba(22,163,74,0.35)] transition-[background-color,transform,box-shadow] duration-200 hover:bg-volt-dark hover:-translate-y-0.5"
              data-testid="nav-level2-badge"
            >
              <Zap className="w-3.5 h-3.5" fill="currentColor" /> Level 2 ASP
            </Link>
            <a
              href={BUSINESS.phoneHref}
              className="inline-flex items-center gap-2 text-ink font-heading text-xs font-bold uppercase tracking-wider px-4 py-3 border border-ink/20 rounded-md transition-colors duration-200 hover:border-volt hover:text-volt"
              data-testid="nav-call-button"
            >
              <Phone className="w-3.5 h-3.5" /> Call Now
            </a>
            <Link to="/quote" className="btn-volt !px-5 !py-3 !text-xs" data-testid="nav-quote-button">
              Request a Quote
            </Link>
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <a
              href={BUSINESS.phoneHref}
              className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-ink/20 text-ink"
              data-testid="mobile-call-button"
              aria-label={`Call LAD CO Electrical on ${BUSINESS.phone}`}
            >
              <Phone className="w-4 h-4" />
            </a>
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-volt text-white"
              data-testid="mobile-menu-button"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] bg-ink dot-texture flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
          >
            <div className="container-site">
              <div className="flex items-center justify-between h-24">
                <Logo onClick={() => setOpen(false)} />
                <button
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center w-11 h-11 border border-white/25 text-white"
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <nav className="flex-1 container-site flex flex-col justify-center gap-1" aria-label="Mobile">
              {[...NAV_LINKS, { label: "Request a Quote", to: "/quote" }].map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                >
                  <Link
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={`group flex items-center justify-between py-4 border-b border-steel font-heading text-2xl font-extrabold uppercase tracking-tight ${
                      link.to === "/quote" ? "text-volt-bright" : "text-white"
                    }`}
                  >
                    {link.label}
                    <ArrowRight className="w-5 h-5 text-volt opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="container-site pb-10 flex flex-col gap-3">
              <a href={BUSINESS.phoneHref} className="btn-ghost-light w-full" data-testid="mobile-menu-call">
                <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
              </a>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt-bright text-center mt-2">
                Level 2 Accredited Service Provider — Northern Rivers NSW
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
